import { zodResolver } from '@hookform/resolvers/zod'
import { createFileRoute, Link } from '@tanstack/react-router'
import type { ComponentProps } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { api } from '@/api/client'
import type { ApiError } from '@/api/me'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'

// The public Workspace request form (09 Onboarding, #227): no sign-in. A Platform admin reviews
// the request and the requester hears back by email.
export const Route = createFileRoute('/sign_up')({
  component: SignUpPage,
})

type Field = 'name' | 'email' | 'business_name' | 'note'
const FIELDS: Field[] = ['name', 'email', 'business_name', 'note']

function SignUpPage() {
  const { t } = useTranslation()
  const createRequest = api.useMutation('post', '/api/workspace_requests')

  const formSchema = z.object({
    name: z.string().trim().min(1, { message: t('signUp.required') }),
    email: z.email({ message: t('signUp.invalidEmail') }),
    business_name: z.string().trim().min(1, { message: t('signUp.required') }),
    note: z.string(),
  })
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { name: '', email: '', business_name: '', note: '' },
  })
  const errors = form.formState.errors

  // A 422 validation_failed names the fields in `details`: show them inline.
  const showFieldErrors = (error: unknown) => {
    const body = (error as ApiError | undefined)?.error
    if (body?.key !== 'validation_failed') return
    for (const field of Object.keys((body.details ?? {}) as Record<string, unknown>)) {
      if (FIELDS.includes(field as Field)) {
        form.setError(field as Field, { message: t(field === 'email' ? 'signUp.invalidEmail' : 'signUp.invalidField') })
      }
    }
  }

  const onSubmit = form.handleSubmit((values) => {
    const note = values.note.trim()
    createRequest.mutate(
      {
        body: {
          workspace_request: {
            name: values.name.trim(),
            email: values.email.trim(),
            business_name: values.business_name.trim(),
            ...(note ? { note } : {}),
          },
        },
      },
      { onError: showFieldErrors },
    )
  })

  const failed =
    createRequest.isError && (createRequest.error as ApiError | undefined)?.error?.key !== 'validation_failed'

  return (
    <main className="flex min-h-svh items-center justify-center p-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle className="font-heading text-xl">{t('signUp.title')}</CardTitle>
          <CardDescription>{t('signUp.subtitle')}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {createRequest.isSuccess ? (
            <p role="status" className="text-sm">
              {t('signUp.received')}
            </p>
          ) : (
            <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
              <p role="alert" aria-live="assertive" className="text-sm text-destructive empty:hidden">
                {failed ? t('signUp.failed') : ''}
              </p>

              <TextField id="name" label={t('signUp.nameLabel')} error={errors.name?.message} autoComplete="name" {...form.register('name')} />
              <TextField
                id="email"
                type="email"
                inputMode="email"
                autoComplete="email"
                label={t('signUp.emailLabel')}
                placeholder={t('signUp.emailPlaceholder')}
                error={errors.email?.message}
                {...form.register('email')}
              />
              <TextField
                id="business_name"
                label={t('signUp.businessNameLabel')}
                autoComplete="organization"
                error={errors.business_name?.message}
                {...form.register('business_name')}
              />
              <div className="flex flex-col gap-2">
                <Label htmlFor="note">{t('signUp.noteLabel')}</Label>
                <Textarea id="note" placeholder={t('signUp.notePlaceholder')} {...form.register('note')} />
              </div>

              <Button type="submit" className="h-11" disabled={createRequest.isPending}>
                {t(createRequest.isPending ? 'signUp.submitting' : 'signUp.submit')}
              </Button>
            </form>
          )}
          <p className="text-center text-sm text-muted-foreground">
            {t('signUp.haveAccess')}{' '}
            <Link to="/login" className="font-medium text-foreground underline underline-offset-4">
              {t('signUp.signIn')}
            </Link>
          </p>
        </CardContent>
      </Card>
    </main>
  )
}

function TextField({
  id,
  label,
  error,
  ...props
}: ComponentProps<typeof Input> & { id: string; label: string; error?: string }) {
  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} className="h-11" aria-invalid={Boolean(error)} aria-describedby={`${id}-error`} {...props} />
      <p id={`${id}-error`} role="alert" className="text-sm text-destructive empty:hidden">
        {error ?? ''}
      </p>
    </div>
  )
}
